import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { DemoStorageService } from '../../core/demo-storage.service';
import type { Contratista, RequisitoContratista, EstadoContratista } from './models/contratistas.model';

const KEY_CONTRATISTAS = 'contratistas';
const KEY_REQUISITOS = 'contratistas_requisitos';

function demoContratistas(): Contratista[] {
  const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
  const d = (daysAgo: number) =>
    new Date(Date.now() - daysAgo * 24 * 3600 * 1000).toISOString().slice(0, 10);
  const next = (daysFromNow: number) =>
    new Date(Date.now() + daysFromNow * 24 * 3600 * 1000).toISOString().slice(0, 10);
  return [
    {
      id: '1',
      razonSocial: 'Servicios de Limpieza ABC S.A.S',
      nitRuc: '900.123.456-7',
      contacto: 'Pedro Gómez',
      email: 'pgomez@abc.com',
      telefono: '300 111 2233',
      servicioContratado: 'Limpieza y aseo',
      fechaIngreso: d(180),
      fechaVigencia: next(185),
      estado: 'vigente',
      evaluacionSSTPrevia: 85,
      observaciones: '',
      createdAt: now,
      updatedAt: now,
    },
    {
      id: '2',
      razonSocial: 'Mantenimiento Industrial XYZ',
      nitRuc: '800.987.654-3',
      contacto: 'Laura Martínez',
      email: 'lmartinez@xyz.com',
      telefono: '310 222 3344',
      servicioContratado: 'Mantenimiento eléctrico',
      fechaIngreso: d(30),
      fechaVigencia: next(335),
      estado: 'evaluacion',
      observaciones: 'Documentación en revisión.',
      createdAt: now,
      updatedAt: now,
    },
  ];
}

function demoRequisitos(): RequisitoContratista[] {
  const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
  const next = (days: number) => new Date(Date.now() + days * 24 * 3600 * 1000).toISOString().slice(0, 10);
  return [
    { id: 'r1', contratistaId: '1', nombre: 'Política SST', cumplido: true, fechaVencimiento: next(365), observaciones: '', createdAt: now, updatedAt: now },
    { id: 'r2', contratistaId: '1', nombre: 'Certificado ARL vigente', cumplido: true, fechaVencimiento: next(200), observaciones: '', createdAt: now, updatedAt: now },
    { id: 'r3', contratistaId: '2', nombre: 'Política SST', cumplido: false, fechaVencimiento: next(30), observaciones: 'Pendiente envío', createdAt: now, updatedAt: now },
  ];
}

const DIAS_ALERTA_VENCIMIENTO = 30;

@Injectable({ providedIn: 'root' })
export class ContratistasService {
  private contratistas: Contratista[] = demoContratistas();
  private requisitos: RequisitoContratista[] = demoRequisitos();

  constructor(private demoStorage: DemoStorageService) {}

  async loadFromDemoStorage(): Promise<void> {
    if (!environment.demo) return;
    const [c, r] = await Promise.all([
      this.demoStorage.get<Contratista[]>(KEY_CONTRATISTAS),
      this.demoStorage.get<RequisitoContratista[]>(KEY_REQUISITOS),
    ]);
    this.contratistas = Array.isArray(c) ? c : demoContratistas();
    this.requisitos = Array.isArray(r) ? r : demoRequisitos();
    await this.persistDemo();
  }

  private persistDemo(): Promise<void> {
    if (!environment.demo) return Promise.resolve();
    return Promise.all([
      this.demoStorage.set(KEY_CONTRATISTAS, this.contratistas),
      this.demoStorage.set(KEY_REQUISITOS, this.requisitos),
    ]).then(() => {}).catch(() => {});
  }

  getContratistas(filters?: { estado?: string }): Contratista[] {
    let list = [...this.contratistas];
    if (filters?.estado) list = list.filter((c) => c.estado === filters.estado);
    return list.sort((a, b) => b.fechaIngreso.localeCompare(a.fechaIngreso));
  }

  getById(id: string): Contratista | null {
    return this.contratistas.find((c) => c.id === id) ?? null;
  }

  createContratista(data: Omit<Contratista, 'id' | 'createdAt' | 'updatedAt'>): Contratista {
    const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
    const nuevo: Contratista = { ...data, id: 'c' + Date.now(), createdAt: now, updatedAt: now };
    this.contratistas.push(nuevo);
    this.persistDemo();
    return { ...nuevo };
  }

  updateContratista(id: string, data: Partial<Omit<Contratista, 'id' | 'createdAt'>>): Contratista | null {
    const c = this.contratistas.find((x) => x.id === id);
    if (!c) return null;
    Object.assign(c, data);
    c.updatedAt = new Date().toISOString().slice(0, 19).replace('T', ' ');
    this.persistDemo();
    return { ...c };
  }

  deleteContratista(id: string): boolean {
    const idx = this.contratistas.findIndex((x) => x.id === id);
    if (idx === -1) return false;
    this.requisitos = this.requisitos.filter((r) => r.contratistaId !== id);
    this.contratistas.splice(idx, 1);
    this.persistDemo();
    return true;
  }

  getRequisitos(contratistaId: string): RequisitoContratista[] {
    return [...this.requisitos.filter((r) => r.contratistaId === contratistaId)];
  }

  addRequisito(contratistaId: string, data: Omit<RequisitoContratista, 'id' | 'contratistaId' | 'createdAt' | 'updatedAt'>): RequisitoContratista {
    const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
    const nuevo: RequisitoContratista = { ...data, id: 'r' + Date.now(), contratistaId, createdAt: now, updatedAt: now };
    this.requisitos.push(nuevo);
    this.persistDemo();
    return { ...nuevo };
  }

  updateRequisito(id: string, data: Partial<Omit<RequisitoContratista, 'id' | 'contratistaId' | 'createdAt'>>): RequisitoContratista | null {
    const r = this.requisitos.find((x) => x.id === id);
    if (!r) return null;
    Object.assign(r, data);
    r.updatedAt = new Date().toISOString().slice(0, 19).replace('T', ' ');
    this.persistDemo();
    return { ...r };
  }

  toggleCumplidoRequisito(id: string): boolean {
    const r = this.requisitos.find((x) => x.id === id);
    if (!r) return false;
    r.cumplido = !r.cumplido;
    r.updatedAt = new Date().toISOString().slice(0, 19).replace('T', ' ');
    this.persistDemo();
    return true;
  }

  deleteRequisito(id: string): boolean {
    const idx = this.requisitos.findIndex((x) => x.id === id);
    if (idx === -1) return false;
    this.requisitos.splice(idx, 1);
    this.persistDemo();
    return true;
  }

  /** Contratistas con vigencia vencida. */
  getVencidos(): Contratista[] {
    const hoy = new Date().toISOString().slice(0, 10);
    return this.contratistas.filter((c) => c.fechaVigencia && c.fechaVigencia < hoy);
  }

  /** Contratistas con vigencia por vencer (próximos N días). */
  getPorVencer(dias = DIAS_ALERTA_VENCIMIENTO): Contratista[] {
    const hoy = new Date();
    const limite = new Date(hoy);
    limite.setDate(limite.getDate() + dias);
    const hoyStr = hoy.toISOString().slice(0, 10);
    const limiteStr = limite.toISOString().slice(0, 10);
    return this.contratistas.filter((c) => c.fechaVigencia && c.fechaVigencia >= hoyStr && c.fechaVigencia <= limiteStr);
  }

  /** Resumen por estado. */
  getResumenEstados(): { estado: EstadoContratista; cantidad: number }[] {
    const counts = new Map<EstadoContratista, number>();
    const estados: EstadoContratista[] = ['evaluacion', 'aprobado', 'rechazado', 'vigente', 'suspendido'];
    estados.forEach((e) => counts.set(e, 0));
    this.contratistas.forEach((c) => counts.set(c.estado, (counts.get(c.estado) ?? 0) + 1));
    return estados.map((estado) => ({ estado, cantidad: counts.get(estado) ?? 0 }));
  }
}
